// Chemin: C:\gestion-copro\src\pages\admin\AffaireFormPage.tsx
// Session 58 : Correction bug coproprietaireId → coproprieteIds[0]
// Session 16 : correction bug undefined pour Firestore

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  CreateAffaireData,
  TypeAffaire,
  PrioriteAffaire,
  GarantieApplicable,
  TYPE_AFFAIRE_LABELS,
  PRIORITE_AFFAIRE_LABELS,
  GARANTIE_LABELS
} from '../../types/affaire.types';
import { InterlocuteurAvecContactPrincipal } from '../../types/interlocuteur.types';
import { createAffaire, getAffaireById, updateAffaire } from '../../services/affaire.service';
import { getInterlocuteurs } from '../../services/interlocuteur.service';

export default function AffaireFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { userProfile, firebaseUser } = useAuth();
  const isEdition = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdition);
  const [error, setError] = useState<string | null>(null);
  const [interlocuteurs, setInterlocuteurs] = useState<InterlocuteurAvecContactPrincipal[]>([]);

  // Champs du formulaire
  const [titre, setTitre] = useState('');
  const [type, setType] = useState<TypeAffaire>('litige_construction');
  const [priorite, setPriorite] = useState<PrioriteAffaire>('normale');
  const [description, setDescription] = useState('');
  const [interlocuteurIds, setInterlocuteurIds] = useState<string[]>([]);
  const [garantieApplicable, setGarantieApplicable] = useState<GarantieApplicable | ''>('');
  const [montantEstime, setMontantEstime] = useState<string>('');

  const coproprieteId = userProfile?.coproprieteIds?.[0];

  // Charger les données en mode édition
  useEffect(() => {
    if (!coproprieteId) return;

    const loadData = async () => {
      try {
        // Charger les interlocuteurs
        const interlocuteursData = await getInterlocuteurs(coproprieteId);
        setInterlocuteurs(interlocuteursData);

        // Charger l'affaire si édition
        if (id) {
          const affaire = await getAffaireById(id);
          if (affaire) {
            setTitre(affaire.titre);
            setType(affaire.type);
            setPriorite(affaire.priorite);
            setDescription(affaire.description);
            setInterlocuteurIds(affaire.interlocuteurIds);
            setGarantieApplicable(affaire.garantieApplicable || '');
            setMontantEstime(affaire.montantEstime?.toString() || '');
          }
        }
      } catch (err) {
        console.error('Erreur chargement données:', err);
        setError('Impossible de charger les données');
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [coproprieteId, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!coproprieteId || !firebaseUser) {
      setError('Session invalide');
      return;
    }

    if (!titre.trim()) {
      setError('Le titre est obligatoire');
      return;
    }

    if (!description.trim()) {
      setError('La description est obligatoire');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Construire l'objet de base (sans champs optionnels)
      const data: CreateAffaireData = {
        coproprieteId,
        titre: titre.trim(),
        type,
        description: description.trim(),
        priorite,
        interlocuteurIds
      };

      // Ajouter les champs optionnels SEULEMENT s'ils ont une valeur
      // (Firestore n'accepte pas undefined)
      if (garantieApplicable) {
        data.garantieApplicable = garantieApplicable as GarantieApplicable;
      }
      if (montantEstime) {
        data.montantEstime = parseFloat(montantEstime);
      }

      if (isEdition && id) {
        // Pour l'update, on utilise undefined pour les champs vides
        await updateAffaire(id, {
          titre: data.titre,
          type: data.type,
          priorite: data.priorite,
          description: data.description,
          interlocuteurIds: data.interlocuteurIds,
          garantieApplicable: garantieApplicable ? garantieApplicable as GarantieApplicable : undefined,
          montantEstime: montantEstime ? parseFloat(montantEstime) : undefined
        });
        navigate(`/admin/affaires/${id}`);
      } else {
        const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur';
        const newId = await createAffaire(data, firebaseUser.uid, userNom);
        navigate(`/admin/affaires/${newId}`);
      }
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleInterlocuteurToggle = (interlocuteurId: string) => {
    setInterlocuteurIds(prev => 
      prev.includes(interlocuteurId)
        ? prev.filter(id => id !== interlocuteurId)
        : [...prev, interlocuteurId]
    );
  };

  // Afficher le champ garantie seulement pour les litiges construction
  const showGarantie = type === 'litige_construction';

  if (!coproprieteId) {
    return (
      <AdminLayout>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Aucune copropriété associée à votre compte.
          </p>
        </div>
      </AdminLayout>
    );
  }

  if (loadingData) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto">
        {/* En-tête */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/affaires')}
            className="text-gray-600 hover:text-gray-900 mb-2 inline-flex items-center"
          >
            &larr; Retour aux affaires
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdition ? 'Modifier l\'affaire' : 'Nouvelle affaire'}
          </h1>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Titre */}
          <div>
            <label htmlFor="titre" className="block text-sm font-medium text-gray-700 mb-1">
              Titre de l'affaire *
            </label>
            <input
              type="text"
              id="titre"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Ex: Infiltrations toiture bâtiment A"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Type et Priorité */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                Type d'affaire *
              </label>
              <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value as TypeAffaire)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {(Object.entries(TYPE_AFFAIRE_LABELS) as [TypeAffaire, string][]).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="priorite" className="block text-sm font-medium text-gray-700 mb-1">
                Priorité
              </label>
              <select
                id="priorite"
                value={priorite}
                onChange={(e) => setPriorite(e.target.value as PrioriteAffaire)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {(Object.entries(PRIORITE_AFFAIRE_LABELS) as [PrioriteAffaire, string][]).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Garantie (si litige construction) */}
          {showGarantie && (
            <div>
              <label htmlFor="garantie" className="block text-sm font-medium text-gray-700 mb-1">
                Garantie applicable
              </label>
              <select
                id="garantie"
                value={garantieApplicable}
                onChange={(e) => setGarantieApplicable(e.target.value as GarantieApplicable | '')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Non déterminée</option>
                {(Object.entries(GARANTIE_LABELS) as [GarantieApplicable, string][]).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                GPA: 1 an après réception | Biennale: 2 ans | Décennale: 10 ans
              </p>
            </div>
          )}

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Décrivez le contexte, les faits constatés, les enjeux..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Montant estimé */}
          <div>
            <label htmlFor="montant" className="block text-sm font-medium text-gray-700 mb-1">
              Montant estimé (optionnel)
            </label>
            <div className="relative">
              <input
                type="number"
                id="montant"
                value={montantEstime}
                onChange={(e) => setMontantEstime(e.target.value)}
                placeholder="0"
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
              />
              <span className="absolute right-4 top-2 text-gray-500">EUR</span>
            </div>
          </div>

          {/* Interlocuteurs concernés */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interlocuteurs concernés
            </label>
            {interlocuteurs.length === 0 ? (
              <p className="text-sm text-gray-500">
                Aucun interlocuteur enregistré. 
                <a href="/admin/interlocuteurs/nouveau" className="text-blue-600 hover:underline ml-1">
                  Créer un interlocuteur
                </a>
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {interlocuteurs.map((interlocuteur) => (
                  <label
                    key={interlocuteur.id}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={interlocuteurIds.includes(interlocuteur.id)}
                      onChange={() => handleInterlocuteurToggle(interlocuteur.id)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">
                        {interlocuteur.raisonSociale}
                      </div>
                      <div className="text-sm text-gray-500">
                        {interlocuteur.type}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/admin/affaires')}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Enregistrement...' : (isEdition ? 'Enregistrer' : 'Créer l\'affaire')}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}